import {
  TApiaFieldValueType,
  TApiaFieldType,
  TApiaFieldPropertiesObj,
  TApiaPossibleValue,
  TComponentSubmitValues,
} from '../types';
import store from '../store';
import { setFieldFocusFromJSApi } from '../store/JSApiSlice';
import IProperty from './constants/IProperty';
import { IIProperty, IApiaField, IApiaForm, TApiaHTMLElement } from './types';
import fieldsStore from '../components/apia/execution/fieldsStore/fieldsStore';
import propsStore from '../store/propsStore/propsStore';
import DataGridField from './fields/DataGridField';
import { ApiaUtil } from '@apia/components';

export default abstract class ApiaField implements IApiaField {
  dateFormat?: string;

  attId: string | null;

  #booleanOptions: string[];

  field: TApiaHTMLElement;

  fldId: string | null;

  fldType: TApiaFieldType | null;

  form: IApiaForm;

  index: number | null;

  onGrid: boolean;

  iProperty: IIProperty;

  name: string | null;

  valueType: TApiaFieldValueType | null;

  get props() {
    if (!this.fldId) {
      console.error(this);
      throw new Error('A field with no ID');
    }
    return propsStore.getFieldProps(this.fldId);
  }

  constructor(field: TApiaHTMLElement, form: IApiaForm, boolOpts: string[]) {
    this.field = field;
    this.form = form;
    this.iProperty = IProperty;
    this.fldType = field.getAttribute('data-type') as TApiaFieldType;
    this.valueType = field.getAttribute(
      'data-value-type',
    ) as TApiaFieldValueType;
    this.name = field.getAttribute('name');
    this.fldId =
      field.getAttribute('id') || field.getAttribute('data-field-id');
    this.attId = field.getAttribute('data-att-id');
    this.onGrid = field.getAttribute(
      'data-field-in-grid',
    ) as unknown as boolean;
    this.index = parseInt(field.getAttribute('data-idx') as string, 10);
    this.#booleanOptions = boolOpts;
    this.dateFormat = field.dataset.dateFormat;
  }

  checkFieldExistsInDocument(fldId: string | null): boolean {
    if (fldId) {
      const cmp = document.querySelector(`[id="${fldId}"]`);
      if (cmp?.id === fldId) {
        return true;
      }
    }

    return false;
  }

  #checkPropExists(name: string): boolean {
    if (name) {
      return Object.keys(this.iProperty).some(
        (k) => this.iProperty[k as keyof IIProperty] === name,
      );
    }
    return false;
  }

  getForm(): IApiaForm | null {
    if (this.checkFieldExistsInDocument(this.fldId)) {
      return this.form;
    }
    return null;
  }

  setValue(
    value: string | boolean | number | string[] | Date | null,
    isAjax?: boolean,
  ): IApiaField | null {
    let val;
    if (value !== null && this.fldId) {
      if (this.form.getProperty(this.iProperty.PROPERTY_FORM_READONLY))
        return this as IApiaField;
      if (value instanceof Date) {
        val = value.toString();
        fieldsStore.getById(this.fldId).setValue(val, {
          synchronize: true,
          setValueOnReadonly: true,
          setLastValidValue: false,
        });
        return this as IApiaField;
      }
      if (isAjax) {
        fieldsStore.getById(this.fldId).setValue(value, {
          synchronize: true,
          setValueOnReadonly: true,
          setLastValidValue: true,
        });
      } else {
        fieldsStore.getById(this.fldId).setValue(value, {
          synchronize: true,
          setValueOnReadonly: true,
          setLastValidValue: false,
        });
      }
    } else if (!this.fldId) {
      console.warn('This field has no fieldId', this);
    }
    return this as IApiaField;
  }

  getValue(): unknown {
    if (!this.fldId) {
      console.warn('This field has no fieldId', this);
    } else if (this.checkFieldExistsInDocument(this.fldId)) {
      return fieldsStore.getById(this.fldId).getValue();
    }
    return null;
  }

  getAttribute(value: string): string {
    return this.getAttribute(value);
  }

  setProperty(
    name: string,
    value:
      | string
      | boolean
      | number
      | string[]
      | number[]
      | TApiaPossibleValue[],
  ) {
    if (this.checkFieldExistsInDocument(this.fldId)) {
      let propertyValue;

      if (this.#checkPropExists(name) && value !== null) {
        // check if property needs boolean value and convert value to bool
        const isBool = this.#booleanOptions.find(
          (prop: string) => prop === name,
        );
        if (isBool) {
          propertyValue = ApiaUtil.instance.parsers.parseBool(value as string);
        } else {
          propertyValue = value;
        }

        if (this.fldId !== null) {
          propsStore.updateField(this.fldId, { [name]: propertyValue });

          if (name === 'required' && !propertyValue) {
            propsStore.updateField(this.fldId, { message: '' });
          }

          return this;
        }
      }
    }
    return null;
  }

  getProperty(
    name: string,
  ): TApiaFieldPropertiesObj | TComponentSubmitValues | null {
    if (this.checkFieldExistsInDocument(this.fldId)) {
      if (typeof this.fldId === 'string' && name !== undefined) {
        if (this.#checkPropExists(name)) {
          const props = propsStore.getFieldProps(this.fldId);
          // check if property a bool
          const isBool = this.#booleanOptions.find(
            (prop: string) => prop === name,
          );
          if (isBool && props[name as keyof typeof props] === undefined) {
            return false;
          }
          return props[name as keyof typeof props] as TApiaFieldPropertiesObj;
        }
      }
    }
    return null;
  }

  getLabel(): string | null | void {
    if (
      this.checkFieldExistsInDocument(this.fldId) &&
      typeof this.fldId === 'string'
    ) {
      const props = propsStore.getFieldProps(this.fldId);
      return props.attributes?.attLabel;
    }
    return null;
  }

  setFocus(): IApiaField | null {
    if (this.checkFieldExistsInDocument(this.fldId)) {
      store.dispatch(
        setFieldFocusFromJSApi({
          focusFieldId: this.fldId,
        }),
      );
      return this as IApiaField;
    }
    return null;
  }

  clearValue(): IApiaField | null | void {
    this.setValue('');
    return this as IApiaField;
  }

  clearGrid(): IApiaField {
    return this as IApiaField;
  }

  isInGrid(): boolean {
    return this.onGrid;
  }

  setError(error: string) {
    fieldsStore.getById(this.fldId ?? '')?.setError(error);
  }

  getColIndex() {
    if (this.isInGrid()) {
      if (this.field.dataset.fieldInGrid === 'true') {
        const fieldData = this.field.id.match(/((?:E|P)_\d+_\d+)_(\d+)_(\d+)/);
        const columnIndex = Number(fieldData?.[2]);
        return Number(columnIndex);
      }
    }

    return null;
  }

  getParentGrid() {
    if (this.isInGrid()) {
      if (this.field.dataset.fieldInGrid === 'true') {
        const fieldData = this.field.id.match(/((?:E|P)_\d+_\d+)_(\d+)_(\d+)/);
        const gridId = fieldData?.[1];

        if (gridId) {
          const grid = this.form.getField(gridId);
          return grid;
        }
      }
    }
    return undefined;
  }

  getNext() {
    if (this.isInGrid()) {
      if (this.field.dataset.fieldInGrid === 'true') {
        const fieldData = this.field.id.match(/((?:E|P)_\d+_\d+)_(\d+)_(\d+)/);
        const gridId = fieldData?.[1];
        const columnIndex = Number(fieldData?.[2]);
        const rowIndex = Number(fieldData?.[3]);

        if (gridId) {
          const grid = this.form.getField(gridId);
          const next = (grid as DataGridField).getRow(rowIndex)[
            columnIndex + 1
          ];
          return next;
        }
      }
    }
    return undefined;
  }

  getPrevious() {
    if (this.isInGrid()) {
      if (this.field.dataset.fieldInGrid === 'true') {
        const fieldData = this.field.id.match(/((?:E|P)_\d+_\d+)_(\d+)_(\d+)/);
        const gridId = fieldData?.[1];
        const columnIndex = Number(fieldData?.[2]);
        const rowIndex = Number(fieldData?.[3]);

        console.log({ gridId, columnIndex, rowIndex });

        if (gridId) {
          const grid = this.form.getField(gridId);
          const next = (grid as DataGridField).getRow(rowIndex)[
            columnIndex - 1
          ];
          return next;
        }
      }
    }
    return undefined;
  }
}
